import React, { memo, useCallback, useEffect, useState } from 'react'
import { Form, Tabs, Collapse, Grid, Badge } from 'antd'
import { useSelector } from 'react-redux'
import { CardTimeline } from '../tab-timeline'
import { setTimelineClear } from '../../../../../store/timeline/actions'
import { getTimeLineData, getTimeLineFiles } from '../../../../../store/timeline/action-creators'
import { setStagePlanClear } from '../../../../../store/stagePlan/actions'
import { createSession, getCardID } from '../../selectors'
import { StagePlan } from '../stage-plan'
import { DataForm } from '../form/form'
import AppChat from '../../../../../components/app-chat'
import { getDataOfType } from '../../../../../../lib/utils/get-data-of-type'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import { CardFiles } from '../tab-files'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { StyledCollapse } from '../../../../../components/v2/field-group/styles'
import { TimelineTab } from '../../styles'
import EntityRelations from '../../../../../components/entity-relations'

interface CardTabsProps {
  onChange?: (key: string) => void
  onTabClick?: (key: string) => void
}

const CardTabs: React.FC<CardTabsProps> = memo((props: CardTabsProps) => {
  const { onChange, onTabClick } = props
  const dispatch = useAppDispatch()
  const id = useSelector(getCardID)
  const screens = Grid.useBreakpoint()
  const isMobile = !screens?.md
  const [activeKey, setActiveKey] = useState<string>('data')
  const [timelineLoaded, setTimelineLoaded] = useState<boolean>(false)
  const [fullTimelineLoaded, setFullTimelineLoaded] = useState<boolean>(false)
  const [unreadCount, setUnreadCount] = useState<number>(0)

  useEffect(() => {
    dispatch(setStagePlanClear())
    return () => {
      dispatch(setTimelineClear())
      dispatch(setStagePlanClear())
    }
  }, [dispatch])

  const [chatData, setChatData] = useState<any>([])
  const [formChat] = Form.useForm()
  const { sessionData } = useSelector(createSession)

  const getChatData = useCallback(async () => {
    const resultChat = await fetchAPI(`/api/claims_request_chat/?filter[claims_request_id]=${id}`)
    setUnreadCount((resultChat || [])?.filter((item: any) => !item.is_read)?.length || 0)
    setChatData(resultChat || [])
  }, [id])

  useEffect(() => {
    if (id) {
      getChatData()
    }
  }, [id, getChatData])

  const onChatSend = async (values: any) => {
    const userId = getDataOfType(sessionData, 'user.id', Number, null)
    await fetchAPI(`/api/claims_request_chat/`, {
      method: 'POST',
      body: JSON.stringify({
        claims_request_id: id,
        manager_id: userId,
        msg: values.message,
      }),
    })
    getChatData()
    formChat.resetFields()
  }

  const handleOnChange = useCallback(
    key => {
      setActiveKey(key)
      if (id) {
        if (key === 'timeline' && !fullTimelineLoaded) {
          dispatch(getTimeLineData('claim', id))
          setTimelineLoaded(true)
          setFullTimelineLoaded(true)
        } else if (key === 'files' && !timelineLoaded) {
          dispatch(getTimeLineFiles('claim', id))
          setTimelineLoaded(true)
        }
      }
      if (onChange) {
        onChange(key)
      }
      if (key === 'chat') {
        getChatData()
      }
    },
    [dispatch, id, timelineLoaded, fullTimelineLoaded, onChange],
  )

  const handleOnTabClick = useCallback(
    key => {
      if (onTabClick) {
        onTabClick(key)
      }
      if (key === 'chat') {
        getChatData()
      }
    },
    [getChatData, onTabClick],
  )

  const handleMessageRead = useCallback(async (messageId: number) => {
    try {
      await fetchAPI(`/api/claims_request_chat/${messageId}`, { method: 'PATCH' })
      setChatData(
        (prev: any[]) =>
          prev?.map(item => (item.id === messageId ? { ...item, is_read: true } : item)) || [],
      )
      setUnreadCount(prev => Math.max(prev - 1, 0))
    } catch (error) {
      console.error('Не удалось отметить сообщение прочитанным', error)
    }
  }, [])

  if (!id) {
    return null
  }

  const timeLineComponent = isMobile ? (
    <>
      <StyledCollapse ghost>
        <Collapse.Panel header={'Прогноз исполнения'} key={'stage-plan-panel'}>
          <StagePlan id={id} hideHeader />
        </Collapse.Panel>
      </StyledCollapse>
      <StyledCollapse defaultActiveKey={['timeline-panel']} ghost>
        <Collapse.Panel header={'Лента событий'} key={'timeline-panel'}>
          <CardTimeline id={id} hideHeader />
        </Collapse.Panel>
      </StyledCollapse>
    </>
  ) : (
    <TimelineTab>
      <StagePlan id={id} />
      <CardTimeline id={id} />
    </TimelineTab>
  )
  const items = [
    {
      key: 'data',
      label: 'Данные заявки',
      children: <DataForm />,
    },
    {
      key: 'timeline',
      label: 'Лента событий',
      children: timeLineComponent,
    },
    {
      key: 'files',
      label: 'Прикрепленные файлы',
      children: <CardFiles id={id} />,
    },
    {
      label:
        unreadCount > 0 ? (
          <Badge count={unreadCount} size="small" offset={[6, 0]} color="rgb(130, 30, 130)">
            Обсуждения по заявке
          </Badge>
        ) : (
          'Обсуждения по заявке'
        ),
      key: 'chat',
      children: (
        <AppChat
          data={chatData}
          form={formChat}
          onSubmit={onChatSend}
          onMessageRead={handleMessageRead}
        />
      ),
    },
    {
      key: 'relations',
      label: 'Связи',
      children: <EntityRelations entityType="claim-request" entityId={id} />,
    },
  ]
  return (
    <Tabs
      onChange={handleOnChange}
      onTabClick={handleOnTabClick}
      activeKey={activeKey}
      items={items}
    />
  )
})

CardTabs.displayName = 'CardTabs'

export default CardTabs
